// applications/dossier services
// Pure async functions only — no React, no hooks, no state.
// Parse DTO -> domain (safeParse) at this boundary; the anti-corruption layer.
import { privateApi } from "@core/libs/axios";
import { parseWith } from "@core/parse";
import { ApplicationSchema, type Application } from "../types";

/**
 * `POST /acquire/applications/` — get-or-create this applicant's dossier for one round.
 *
 * **Not "create". Get-or-create**, and that is the documented contract rather than a convenience:
 * `201` when the first draft is made, `200` when this founder already has one in this round and the
 * round still accepts applications. Both return the full DTO. A database constraint enforces one
 * Application per founder per round, including under concurrent requests.
 *
 * That idempotence is what makes it safe to call from a route loader on every visit, which is how
 * this step uses it — a re-entry, a refresh and a bookmark all resolve to the same dossier instead
 * of a second one. It is also why there is no "do I already have one?" read first: this call IS
 * that read, and asking separately would be two round-trips and a race.
 *
 * `privateApi`, not `api`: it identifies the founder from the session, and the dossier belongs to
 * them. The round id is public and comes from the URL; the ownership comes from the token.
 *
 * Failures the caller must expect:
 * - `409 intake_closed` — the deadline passed between reading the round and arriving here. Public
 *   discovery is advisory; THIS call is authoritative, because the deadline can pass in between.
 * - `403` — the caller holds no applicant surface.
 * - `400` — the round id is not a UUID.
 */
export async function createApplication(
  intakeRoundId: string
): Promise<Application> {
  const dto = await privateApi
    .post<unknown>("/acquire/applications/", {
      intake_round_id: intakeRoundId,
    })
    .then((r) => r.data);

  return parseWith(ApplicationSchema, dto, "POST /acquire/applications/");
}

/**
 * `GET /acquire/applications/{id}/?locale=` — one dossier, as the applicant's own.
 *
 * Owner-scoped server-side: a founder can read only their own, and a mismatch is a `404` rather
 * than a `403`, so the endpoint does not confirm that someone else's dossier exists.
 *
 * `locale` is sent explicitly because the response embeds the localized schema, labels and legal
 * copy. The query key carries the same value.
 */
export async function getApplication(
  applicationId: string,
  locale: string
): Promise<Application> {
  const dto = await privateApi
    .get<unknown>(`/acquire/applications/${applicationId}/`, {
      params: { locale },
    })
    .then((r) => r.data);

  return parseWith(ApplicationSchema, dto, "GET /acquire/applications/{id}/");
}

/**
 * `PATCH /acquire/applications/{id}/founder/` — save some or all of the founder tab.
 *
 * A partial update: send only the keys that changed. Every call carries `expected_revision`, and the
 * response is the FULL application DTO with the new revision, recalculated progress, fresh
 * eligibility warnings and a rebuilt missing-items list. Replace local state from it wholesale
 * rather than merging field by field — the server recomputes more than was sent.
 *
 * Failures the caller must expect:
 * - `409 stale_application_revision` — someone (another tab, a queued request) wrote first. The body
 *   carries `current_revision`, so recovery does not need a refetch.
 * - `400 application_profile_invalid` — a value the frozen catalogue does not accept, keyed by field.
 * - `409 intake_closed` / `409 application_frozen` — the round shut, or the dossier was submitted.
 *
 * ⚠️ The response is localized to the USER'S PROFILE language, not to `?locale=` — the view passes
 * `request.user.preferred_language` and ignores the query param, unlike the GET. So localized
 * strings in a mutation response can be in a different language from the page. Values, revision and
 * progress are unaffected; warning COPY is owned client-side for this reason (see `warnings.ts`).
 */
export async function patchFounder(
  applicationId: string,
  expectedRevision: number,
  values: Record<string, unknown>
): Promise<Application> {
  const dto = await privateApi
    .patch<unknown>(`/acquire/applications/${applicationId}/founder/`, {
      expected_revision: expectedRevision,
      ...values,
    })
    .then((r) => r.data);

  return parseWith(ApplicationSchema, dto, "PATCH /applications/{id}/founder/");
}

/**
 * `PATCH /acquire/applications/{id}/project/` — save some or all of the project tab.
 *
 * Same partial-update, same-full-DTO-response contract as `patchFounder` — see that function's
 * doc comment for the shared caveats (localized to the user's profile language, not `?locale=`;
 * `409 stale_application_revision` carries the real revision; `400 application_profile_invalid`
 * carries field-keyed errors).
 */
export async function patchProject(
  applicationId: string,
  expectedRevision: number,
  values: Record<string, unknown>
): Promise<Application> {
  const dto = await privateApi
    .patch<unknown>(`/acquire/applications/${applicationId}/project/`, {
      expected_revision: expectedRevision,
      ...values,
    })
    .then((r) => r.data);

  return parseWith(ApplicationSchema, dto, "PATCH /applications/{id}/project/");
}

/**
 * `PATCH /acquire/applications/{id}/team/` — save the flat team fields (`mode`,
 * `other_candidature_code`, `owner_rationale`, `named_members_consent_confirmed`). Same
 * partial-update, same-full-DTO-response contract as `patchFounder`/`patchProject`.
 *
 * Member add/edit/delete is NOT part of this body — three separate endpoints below, because
 * `members` is a real sub-resource with its own identity, not a schema field.
 *
 * ⚠️ Switching `mode` to `SOLO` while members exist is rejected: `409 team_members_present_for_solo`.
 */
export async function patchTeam(
  applicationId: string,
  expectedRevision: number,
  values: Record<string, unknown>
): Promise<Application> {
  const dto = await privateApi
    .patch<unknown>(`/acquire/applications/${applicationId}/team/`, {
      expected_revision: expectedRevision,
      ...values,
    })
    .then((r) => r.data);

  return parseWith(ApplicationSchema, dto, "PATCH /applications/{id}/team/");
}

/**
 * `PATCH /acquire/applications/{id}/practical/` — save some or all of the practical tab. Same
 * partial-update, same-full-DTO-response contract as `patchFounder`/`patchProject`/`patchTeam`.
 */
export async function patchPractical(
  applicationId: string,
  expectedRevision: number,
  values: Record<string, unknown>
): Promise<Application> {
  const dto = await privateApi
    .patch<unknown>(`/acquire/applications/${applicationId}/practical/`, {
      expected_revision: expectedRevision,
      ...values,
    })
    .then((r) => r.data);

  return parseWith(
    ApplicationSchema,
    dto,
    "PATCH /applications/{id}/practical/"
  );
}

/**
 * `PUT /acquire/applications/{id}/consents/{consentType}/` — accept or decline one legal
 * agreement (US-E3-10). Same full-DTO-response contract as the section PATCHes. `locale` is
 * required by the backend (records WHICH language the applicant read when they agreed — the
 * `accepted_locale` on `ConsentSchema`), and is the app's own UI locale, not `?locale=` on the
 * GET — matching how every other mutation response is localized to the user's profile language
 * regardless of what was requested.
 */
export async function putConsent(
  applicationId: string,
  consentType: string,
  expectedRevision: number,
  accepted: boolean,
  locale: string
): Promise<Application> {
  const dto = await privateApi
    .put<unknown>(
      `/acquire/applications/${applicationId}/consents/${consentType}/`,
      {
        expected_revision: expectedRevision,
        accepted,
        locale,
      }
    )
    .then((r) => r.data);

  return parseWith(
    ApplicationSchema,
    dto,
    "PUT /applications/{id}/consents/{consentType}/"
  );
}

/**
 * `POST /acquire/applications/{id}/submit/` — the one irreversible action in this whole module.
 *
 * Synchronous: on success the response already carries `status: "SUBMITTED"` and a real
 * `dossier_number` — no polling, no separate confirmation fetch. Fails `409` with
 * `application_incomplete` (details carry the same `missing_items` shape the review screen
 * already renders — the two are deliberately the same list, not a second source of truth) or
 * `email_verification_required` if the applicant's own email was never confirmed.
 */
export async function submitApplication(
  applicationId: string,
  expectedRevision: number
): Promise<Application> {
  const dto = await privateApi
    .post<unknown>(`/acquire/applications/${applicationId}/submit/`, {
      expected_revision: expectedRevision,
    })
    .then((r) => r.data);

  return parseWith(ApplicationSchema, dto, "POST /applications/{id}/submit/");
}

/**
 * `POST /acquire/applications/{id}/team/members/` — add one named person.
 *
 * `name` and `role` are the only values required at creation; `time_commitment_code`,
 * `capital_distribution_code` and `capital_percentage` may follow later through
 * `patchTeamMember`. At most five members per application (`409 team_member_limit_reached`), and
 * only while `team.mode === "MULTIPLE"` (`409 team_members_require_multiple_mode`).
 *
 * ⚠️ Adding, editing or removing ANY member clears the aggregate
 * `named_members_consent_confirmed` server-side — the response already reflects that, so nothing
 * client-side needs to re-derive it.
 */
export async function createTeamMember(
  applicationId: string,
  expectedRevision: number,
  values: { name: string; role: string }
): Promise<Application> {
  const dto = await privateApi
    .post<unknown>(`/acquire/applications/${applicationId}/team/members/`, {
      expected_revision: expectedRevision,
      ...values,
    })
    .then((r) => r.data);

  return parseWith(
    ApplicationSchema,
    dto,
    "POST /applications/{id}/team/members/"
  );
}

/**
 * `PATCH /acquire/applications/{id}/team/members/{memberId}/` — edit one named person.
 * Same partial-update contract as the section PATCHes; same consent-clearing side effect as
 * `createTeamMember`.
 */
export async function patchTeamMember(
  applicationId: string,
  memberId: string,
  expectedRevision: number,
  values: Record<string, unknown>
): Promise<Application> {
  const dto = await privateApi
    .patch<unknown>(
      `/acquire/applications/${applicationId}/team/members/${memberId}/`,
      { expected_revision: expectedRevision, ...values }
    )
    .then((r) => r.data);

  return parseWith(
    ApplicationSchema,
    dto,
    "PATCH /applications/{id}/team/members/{memberId}/"
  );
}

/**
 * `DELETE /acquire/applications/{id}/team/members/{memberId}/` — remove one named person. Same
 * consent-clearing side effect as `createTeamMember`. The response is still the full Application
 * DTO (not empty), like every other mutation in this file.
 */
export async function deleteTeamMember(
  applicationId: string,
  memberId: string,
  expectedRevision: number
): Promise<Application> {
  const dto = await privateApi
    .delete<unknown>(
      `/acquire/applications/${applicationId}/team/members/${memberId}/`,
      { data: { expected_revision: expectedRevision } }
    )
    .then((r) => r.data);

  return parseWith(
    ApplicationSchema,
    dto,
    "DELETE /applications/{id}/team/members/{memberId}/"
  );
}

/**
 * `PUT /acquire/applications/{id}/answers/{questionId}/` — save one authored answer.
 *
 * One call per question, unlike the founder profile which batches. That is the contract, not a
 * choice: answers are addressed individually by question id.
 *
 * `value` must be JSON-NATIVE and match the question's `input_type` — a number for `NUMBER`, a
 * boolean for `CHECKBOX`, an array of option values for `MULTI_SELECT`. Sending an HTML input's
 * string for a number is `answer_invalid`.
 */
export async function putAnswer(
  applicationId: string,
  questionId: string,
  expectedRevision: number,
  value: unknown
): Promise<Application> {
  const dto = await privateApi
    .put<unknown>(
      `/acquire/applications/${applicationId}/answers/${questionId}/`,
      { expected_revision: expectedRevision, value }
    )
    .then((r) => r.data);

  return parseWith(
    ApplicationSchema,
    dto,
    "PUT /applications/{id}/answers/{questionId}/"
  );
}

// The upload sequence (intent → direct-to-storage → complete) moved to
// `@modules/applications/_shared` — `respond` (fulfilling a coordinator's information request)
// needs the exact same three calls, just with an extra `informationRequestId` threaded through.
// Re-exported here so nothing importing these from `dossier`'s own barrel has to change.
export {
  createUploadIntent,
  completeDocumentUpload,
  uploadDocument,
} from "@modules/applications/_shared";

/**
 * `DELETE /acquire/applications/{id}/documents/{documentId}/` — soft-deletes the document
 * (status → `DELETED`, object storage cleanup enqueued server-side). Returns nothing: unlike
 * every mutation above, this endpoint responds `204 No Content` even though the service function
 * behind it DOES bump the revision — the caller must refetch the application afterward to see it.
 */
export async function deleteApplicationDocument(
  applicationId: string,
  documentId: string,
  expectedRevision: number
): Promise<void> {
  await privateApi.delete<unknown>(
    `/acquire/applications/${applicationId}/documents/${documentId}/`,
    { data: { expected_revision: expectedRevision } }
  );
}
